/**
 * Pure sync conflict-resolution helpers.
 *
 * Kept isolated from `dexieFirebaseSync.ts` so they can be unit-tested in a
 * Node environment without booting Firebase or Dexie. The full sync pipeline
 * composes these with real Dexie/Firestore calls.
 */

// ============================================================================
// PROGRAM SELECTION MERGE
// ============================================================================

export interface ProgramSelectionData {
  programId: string;
  programLevel?: string;
  startDate: string;            // ISO string
  currentWeek: number;
  currentDay: number;
  completedDays?: { week: number; day: number }[];
  skippedDays?: { week: number; day: number }[];
  /**
   * Tombstones for days the user explicitly UN-skipped.
   *
   * skippedDays is a union, and a union cannot express deletion: removing an
   * entry locally is undone by the next sync because the other side still has
   * it. That silently defeated both setCurrentDay's auto-unskip and the
   * "Dismiss all" missed-drills control — the user cleared them and they came
   * back on the next tick. A day listed here is suppressed from the merged
   * skip list; re-skipping a day must remove it from here.
   */
  unskippedDays?: { week: number; day: number }[];
  syncVersion?: number;
  updatedAt?: string;           // ISO string
  restartedAt?: string;         // ISO string — when set, this side wins unconditionally
  /**
   * When the user EXPLICITLY chose this position (day picker / navigator /
   * "Resume Here"), as opposed to the pointer advancing on its own.
   *
   * The max-scalar rule below is the right default for multi-device sync — it
   * is why opening the app on a second device has never cost anyone progress.
   * But every local write goes through this merge, so without an escape hatch
   * a deliberate move BACKWARD is compared against the stale further-ahead
   * remote and silently reverted inside the same save. This is that escape
   * hatch, resolved the same way `restartedAt` is: it wins only when it is
   * newer than the other side's last update, so a week-old manual jump can
   * never drag back a user who has since trained elsewhere.
   */
  positionSetAt?: string;       // ISO string
  /**
   * The position `positionSetAt` refers to.
   *
   * Without these, an intent stamp was only usable while it was strictly
   * newer than the other side's stamp — i.e. exactly once, before it synced.
   * Recording the position alongside the timestamp lets both sides resolve to
   * the same answer after the stamp has propagated.
   */
  positionWeek?: number;
  positionDay?: number;
  /**
   * When the user actually began, preserved because `startDate` is realigned
   * by jumpToFutureDay. Never regenerated, so the merge keeps the EARLIEST
   * value either side knows — losing it loses it permanently.
   */
  originalStartDate?: string;   // ISO string
}

/**
 * Merge two program selection states with commutative conflict resolution.
 * Same result regardless of which device syncs first.
 *
 * Rules:
 * - programId: if different, the one with later startDate wins (user switched).
 *   If same startDate, later updatedAt wins.
 * - currentWeek/currentDay: an explicit `positionSetAt` newer than the other
 *   side's updatedAt wins (the user chose it); otherwise convert to scalar and
 *   take MAX (never regress). A restart still outranks both.
 * - completedDays: union of both sets
 * - skippedDays: union of both sets, MINUS merged completedDays, MINUS
 *   merged unskippedDays tombstones
 * - unskippedDays: union of both sets, MINUS anything currently skipped by the
 *   newer side (a re-skip retires the tombstone)
 * - startDate: keep the one matching the winning programId
 * - syncVersion: max + 1
 * - Scope arrays to a sane week range (drop corrupt entries, see maxWeeks)
 */
export function mergeProgramSelection(
  local: ProgramSelectionData | null | undefined,
  remote: ProgramSelectionData | null | undefined,
  /**
   * Upper bound for the sanity filter on completedDays/skippedDays.
   *
   * This used to default to 13, and BOTH production call sites pass two
   * arguments — so the default always applied. Every save therefore deleted a
   * longer program's history past week 13, permanently and silently: the
   * prenatal program is 37 weeks and HST is 14.
   *
   * The filter exists to drop CORRUPT entries, not to enforce program length,
   * so the default is now one year — comfortably past the longest real program
   * (prenatal, 37 weeks) while still catching garbage. Callers that know the
   * true duration should still pass it; a program longer than this must.
   */
  maxWeeks: number = 53
): ProgramSelectionData | null {
  // If one side is missing, return the other
  if (!local && !remote) return null;
  if (!local) return remote!;
  if (!remote) return local!;

  // --- Determine winning programId ---
  let winner: ProgramSelectionData;
  let loser: ProgramSelectionData;

  if (local.programId !== remote.programId) {
    // Different programs: later startDate wins (user switched programs)
    const localStart = new Date(local.startDate).getTime();
    const remoteStart = new Date(remote.startDate).getTime();
    if (localStart !== remoteStart) {
      winner = localStart > remoteStart ? local : remote;
      loser = localStart > remoteStart ? remote : local;
    } else {
      // Same startDate, later updatedAt wins
      const localUpdated = new Date(local.updatedAt || '1970-01-01').getTime();
      const remoteUpdated = new Date(remote.updatedAt || '1970-01-01').getTime();
      winner = localUpdated >= remoteUpdated ? local : remote;
      loser = localUpdated >= remoteUpdated ? remote : local;
    }
  } else {
    // Same program: we'll merge positions; winner/loser don't matter for programId
    winner = local;
    loser = remote;
  }

  const sameProgramId = local.programId === remote.programId;

  // --- Restart detection: whichever side has a newer restartedAt wins unconditionally.
  // Local restart: user restarted on this device, remote still has old progress.
  // Remote restart: user restarted on another device, local still has old progress. ---
  const localRestartedAt = local.restartedAt ? new Date(local.restartedAt).getTime() : 0;
  const remoteRestartedAt = remote.restartedAt ? new Date(remote.restartedAt).getTime() : 0;
  const localUpdatedAt = local.updatedAt ? new Date(local.updatedAt).getTime() : 0;
  const remoteUpdatedAt = remote.updatedAt ? new Date(remote.updatedAt).getTime() : 0;
  const isLocalRestart = localRestartedAt > 0 && localRestartedAt > remoteUpdatedAt;
  const isRemoteRestart = remoteRestartedAt > 0 && remoteRestartedAt > localUpdatedAt;
  const isRestart = isLocalRestart || isRemoteRestart;
  const restartSource = isLocalRestart ? local : remote;

  // --- Merge position: take MAX scalar (never regress) ---
  const localScalar = (local.currentWeek - 1) * 7 + local.currentDay;
  const remoteScalar = (remote.currentWeek - 1) * 7 + remote.currentDay;

  let mergedWeek: number;
  let mergedDay: number;

  // --- Intentional position writes ---
  // An explicit user choice of position beats the max-scalar rule, but only
  // while it is fresher than the other side's last update — resolved exactly
  // like restartedAt above so the merge stays commutative (same answer no
  // matter which device syncs first).
  const localPosAt = local.positionSetAt ? new Date(local.positionSetAt).getTime() : 0;
  const remotePosAt = remote.positionSetAt ? new Date(remote.positionSetAt).getTime() : 0;

  /**
   * ONE-SHOT INTENT BUG (fixed 2026-08-11).
   *
   * These conditions used to be `localPosAt > remotePosAt && localPosAt >
   * remoteUpdatedAt`, which worked exactly once. As soon as the intent write
   * synced up, BOTH sides carried the same `positionSetAt`, so the strict
   * `>` between them went false and the intent could no longer beat its own
   * synced copy. The merge fell through to max-scalar never-regress and
   * silently reverted any BACKWARD move — which is what "Resume Here" on a
   * skipped day always is.
   *
   * Live evidence from production at the time of the fix:
   *   positionSetAt 2026-08-11T15:15:58Z
   *   updatedAt     2026-08-11T16:39:39Z   (84 minutes newer)
   * Any later write bumps `updatedAt` without re-stamping the intent, so the
   * user's chosen day lost every subsequent merge.
   *
   * The stamp now carries the position it refers to (`positionWeek`/
   * `positionDay`), so an intent stays self-describing after syncing. Both
   * sides holding the same stamp agree on the same answer, which keeps the
   * merge commutative. Falls back to the side's own currentWeek/Day for
   * records written before the fields existed.
   */
  const newestPosAt = Math.max(localPosAt, remotePosAt);
  /**
   * Freshness is RELATIVE, never wall-clock. The stamp must still be newer
   * than whatever the OTHER side did after it, so a stale jump can't drag back
   * a user who has since trained on — but an old stamp that nothing has
   * superseded stays valid indefinitely. (An absolute TTL here would silently
   * expire a legitimate position and would make the merge non-deterministic
   * over time, which is exactly what a commutative merge must not be.)
   *
   * A side carrying the same stamp is not "moving since" — it is the intent
   * having propagated, which is the whole case this fix exists for.
   */
  const otherSideMovedSince = newestPosAt === localPosAt
    ? remoteUpdatedAt > newestPosAt && remotePosAt !== newestPosAt
    : localUpdatedAt > newestPosAt && localPosAt !== newestPosAt;
  const isIntent = sameProgramId && newestPosAt > 0 && !otherSideMovedSince;
  // Prefer whichever side carries the newest stamp; on a tie they agree.
  const intentSource = localPosAt >= remotePosAt ? local : remote;

  if (isRestart) {
    // Restart: the side that restarted wins unconditionally, don't take max
    mergedWeek = restartSource.currentWeek;
    mergedDay = restartSource.currentDay;
  } else if (isIntent) {
    // The user picked this day. Honour it even when it moves backward.
    mergedWeek = intentSource.positionWeek ?? intentSource.currentWeek;
    mergedDay = intentSource.positionDay ?? intentSource.currentDay;
  } else if (sameProgramId) {
    // Same program: take the further-ahead position
    if (localScalar >= remoteScalar) {
      mergedWeek = local.currentWeek;
      mergedDay = local.currentDay;
    } else {
      mergedWeek = remote.currentWeek;
      mergedDay = remote.currentDay;
    }
  } else {
    // Different programs: use the winner's position
    mergedWeek = winner.currentWeek;
    mergedDay = winner.currentDay;
  }

  // --- Merge completedDays: union of both sets (only if same program) ---
  const completedSet = new Map<string, { week: number; day: number }>();
  const addCompleted = (arr?: { week: number; day: number }[]) => {
    for (const d of arr || []) {
      completedSet.set(`${d.week}-${d.day}`, d);
    }
  };

  if (isRestart) {
    // Restart: only use the restart source's (empty) arrays, don't union with old data
    addCompleted(restartSource.completedDays);
  } else if (sameProgramId) {
    addCompleted(local.completedDays);
    addCompleted(remote.completedDays);
  } else {
    // Different program: only keep winner's data
    addCompleted(winner.completedDays);
  }

  // --- Merge skippedDays: union MINUS completedDays (only if same program) ---
  const skippedSet = new Map<string, { week: number; day: number }>();
  const addSkipped = (arr?: { week: number; day: number }[]) => {
    for (const d of arr || []) {
      const key = `${d.week}-${d.day}`;
      if (!completedSet.has(key)) {
        skippedSet.set(key, d);
      }
    }
  };

  if (isRestart) {
    addSkipped(restartSource.skippedDays);
  } else if (sameProgramId) {
    addSkipped(local.skippedDays);
    addSkipped(remote.skippedDays);
  } else {
    addSkipped(winner.skippedDays);
  }

  // --- Merge unskippedDays: union of tombstones, then suppress skips ---
  // A day the user explicitly un-skipped must not be resurrected by the other
  // side's copy of the skip list. Re-skipping retires the tombstone: the side
  // whose skip list is NEWER wins for that day.
  const unskippedSet = new Map<string, { week: number; day: number }>();
  const addUnskipped = (arr?: { week: number; day: number }[]) => {
    for (const d of arr || []) unskippedSet.set(`${d.week}-${d.day}`, d);
  };

  if (isRestart) {
    addUnskipped(restartSource.unskippedDays);
  } else if (sameProgramId) {
    addUnskipped(local.unskippedDays);
    addUnskipped(remote.unskippedDays);
    // A re-skip on the FRESHER side retires that day's tombstone, so a day can
    // be skipped again after being un-skipped.
    const fresher = localUpdatedAt >= remoteUpdatedAt ? local : remote;
    const staler = localUpdatedAt >= remoteUpdatedAt ? remote : local;
    for (const d of fresher.skippedDays || []) {
      const key = `${d.week}-${d.day}`;
      const tombstonedOnStaler = (staler.unskippedDays || [])
        .some(u => `${u.week}-${u.day}` === key);
      const stillTombstonedOnFresher = (fresher.unskippedDays || [])
        .some(u => `${u.week}-${u.day}` === key);
      if (tombstonedOnStaler && !stillTombstonedOnFresher) unskippedSet.delete(key);
    }
  } else {
    addUnskipped(winner.unskippedDays);
  }

  Array.from(unskippedSet.keys()).forEach(key => skippedSet.delete(key));

  // --- Scope arrays to valid week range ---
  const filterByWeekRange = (arr: { week: number; day: number }[]) =>
    arr.filter(d => d.week >= 1 && d.week <= maxWeeks && d.day >= 1 && d.day <= 7);

  const mergedCompleted = filterByWeekRange(Array.from(completedSet.values()));
  const mergedSkipped = filterByWeekRange(Array.from(skippedSet.values()));
  // Tombstones for days that are now completed are dead weight — completion
  // already removes them from the skip list.
  const mergedUnskipped = filterByWeekRange(Array.from(unskippedSet.values()))
    .filter(d => !completedSet.has(`${d.week}-${d.day}`));

  // --- syncVersion: max of both + 1 ---
  const mergedSyncVersion = Math.max(local.syncVersion || 0, remote.syncVersion || 0) + 1;

  return {
    programId: winner.programId,
    programLevel: winner.programLevel || local.programLevel || remote.programLevel,
    startDate: isRestart ? restartSource.startDate : winner.startDate,
    currentWeek: mergedWeek,
    currentDay: mergedDay,
    completedDays: mergedCompleted,
    skippedDays: mergedSkipped,
    ...(mergedUnskipped.length > 0 ? { unskippedDays: mergedUnskipped } : {}),
    syncVersion: mergedSyncVersion,
    updatedAt: new Date().toISOString(),
    // Keep the EARLIEST known original start. It is captured once and never
    // regenerated, so a merge that drops it destroys it — and only one device
    // may have witnessed the jump that triggered the capture.
    ...(() => {
      const candidates = [local.originalStartDate, remote.originalStartDate]
        .filter((d): d is string => !!d && !isNaN(new Date(d).getTime()));
      if (candidates.length === 0) return {};
      return {
        originalStartDate: candidates.reduce((a, b) =>
          new Date(a).getTime() <= new Date(b).getTime() ? a : b
        ),
      };
    })(),
    ...(isRestart && restartSource.restartedAt ? { restartedAt: restartSource.restartedAt } : {}),
    // Carry the winning intent forward so a third device syncing later resolves
    // to the same position instead of reverting to max-scalar.
    ...(isIntent && intentSource.positionSetAt ? { positionSetAt: intentSource.positionSetAt } : {}),
  };
}

/**
 * Compare two ProgramSelectionData for equality (ignoring syncVersion/updatedAt).
 */
export function programSelectionEquals(
  a: ProgramSelectionData | null,
  b: ProgramSelectionData | null
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.programId !== b.programId) return false;
  if (a.currentWeek !== b.currentWeek || a.currentDay !== b.currentDay) return false;
  if (a.startDate !== b.startDate) return false;

  const aCompletedArr = (a.completedDays || []).map(d => `${d.week}-${d.day}`);
  const bCompleted = new Set((b.completedDays || []).map(d => `${d.week}-${d.day}`));
  if (aCompletedArr.length !== bCompleted.size) return false;
  for (let i = 0; i < aCompletedArr.length; i++) { if (!bCompleted.has(aCompletedArr[i])) return false; }

  const aSkippedArr = (a.skippedDays || []).map(d => `${d.week}-${d.day}`);
  const bSkipped = new Set((b.skippedDays || []).map(d => `${d.week}-${d.day}`));
  if (aSkippedArr.length !== bSkipped.size) return false;
  for (let i = 0; i < aSkippedArr.length; i++) { if (!bSkipped.has(aSkippedArr[i])) return false; }

  return true;
}

// ============================================================================
// DOWNLOAD RECONCILIATION
// ============================================================================

/**
 * Minimum shape a record must satisfy for download reconciliation.
 */
export interface Reconcilable {
  id: string;
  updatedAt: Date;
  syncStatus?: 'local' | 'pending' | 'synced' | string;
}

/**
 * Return only the remote records that are safe to write to Dexie.
 *
 * A remote record is SKIPPED when the local Dexie entry has unsynced changes
 * (syncStatus `local` or `pending`) AND a strictly-newer `updatedAt` than the
 * remote. Those local edits would otherwise be silently overwritten; instead
 * they survive and get pushed on the next upload cycle, where Firestore's
 * per-field merge finishes reconciliation.
 *
 * `tableGet` is a thin facade over `db.<table>.get` so callers can inject a
 * mock in tests.
 */
export async function filterPreserveLocalPending<T extends Reconcilable>(
  tableGet: (id: string) => Promise<T | undefined>,
  remoteItems: T[],
  label: string
): Promise<T[]> {
  if (remoteItems.length === 0) return remoteItems;

  const kept: T[] = [];
  let skipped = 0;
  const toMs = (v: unknown): number => {
    if (!v) return 0;
    if (v instanceof Date) return v.getTime();
    const d = new Date(v as any);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  for (const remote of remoteItems) {
    const local = await tableGet(remote.id);
    if (!local) {
      kept.push(remote);
      continue;
    }
    const localPending = local.syncStatus === 'local' || local.syncStatus === 'pending';
    if (!localPending) {
      kept.push(remote);
      continue;
    }
    if (toMs(local.updatedAt) > toMs(remote.updatedAt)) {
      skipped++;
      continue;
    }
    kept.push(remote);
  }

  if (skipped > 0) {
    // eslint-disable-next-line no-console
    console.log(`[sync] Preserved ${skipped} unsynced local ${label} edit${skipped === 1 ? '' : 's'} (newer than remote)`);
  }
  return kept;
}
